import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookie, faPlus, faTrash, faFileImport, faEdit, faSave, faTimes, faGlobe, faFolder, faComment, faInfoCircle, faSync } from '@fortawesome/free-solid-svg-icons';
import proxyService from '../../services/proxyService';
import { toast } from 'react-toastify';

/**
 * Cookie Manager for Proxy Sessions
 * Allows importing and managing cookies that are automatically injected into all proxy requests
 */
const ProxyCookieManager = ({ sessionId, onCookiesChange }) => {
  const [cookies, setCookies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editingCookie, setEditingCookie] = useState(null);

  // Form state for adding/editing cookie
  const [cookieForm, setCookieForm] = useState({
    name: '',
    value: '',
    domain: '',
    path: '/',
    secure: true,
    httpOnly: false,
    sameSite: 'Lax',
    comment: ''
  });

  // Bulk import JSON
  const [bulkImportJson, setBulkImportJson] = useState('');

  useEffect(() => {
    if (sessionId) {
      loadCookies();
    }
  }, [sessionId]);

  const loadCookies = async () => {
    try {
      setLoading(true);
      const data = await proxyService.getCookies(sessionId);
      setCookies(data);
      onCookiesChange?.(data);
    } catch (err) {
      console.error('Failed to load cookies:', err);
      toast.error('Failed to load cookies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCookie = async () => {
    if (!cookieForm.name || !cookieForm.value) {
      toast.error('Cookie name and value are required');
      return;
    }

    try {
      setLoading(true);
      await proxyService.importCookies(sessionId, [cookieForm], cookieForm.comment || 'Manually added');
      toast.success('Cookie added successfully');
      setShowAddForm(false);
      setCookieForm({
        name: '',
        value: '',
        domain: '',
        path: '/',
        secure: true,
        httpOnly: false,
        sameSite: 'Lax',
        comment: ''
      });
      await loadCookies();
    } catch (err) {
      console.error('Failed to add cookie:', err);
      toast.error(`Failed to add cookie: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCookie = async () => {
    if (!editingCookie) return;

    try {
      setLoading(true);
      // Delete old cookie and add updated one
      await proxyService.deleteCookie(sessionId, editingCookie.originalName, editingCookie.originalDomain);
      await proxyService.importCookies(sessionId, [cookieForm], cookieForm.comment || 'Updated');
      toast.success('Cookie updated successfully');
      setEditingCookie(null);
      setCookieForm({
        name: '',
        value: '',
        domain: '',
        path: '/',
        secure: true,
        httpOnly: false,
        sameSite: 'Lax',
        comment: ''
      });
      await loadCookies();
    } catch (err) {
      console.error('Failed to update cookie:', err);
      toast.error(`Failed to update cookie: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCookie = async (cookieName, domain) => {
    if (!window.confirm(`Delete cookie "${cookieName}"?`)) return;

    try {
      setLoading(true);
      await proxyService.deleteCookie(sessionId, cookieName, domain);
      toast.success('Cookie deleted');
      await loadCookies();
    } catch (err) {
      console.error('Failed to delete cookie:', err);
      toast.error(`Failed to delete cookie: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete ALL cookies?')) return;

    try {
      setLoading(true);
      await proxyService.clearCookies(sessionId);
      toast.success('All cookies cleared');
      await loadCookies();
    } catch (err) {
      console.error('Failed to clear cookies:', err);
      toast.error(`Failed to clear cookies: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    try {
      const parsed = JSON.parse(bulkImportJson);
      const cookieArray = Array.isArray(parsed) ? parsed : [parsed];

      setLoading(true);
      await proxyService.importCookies(sessionId, cookieArray, 'Bulk import');
      toast.success(`Imported ${cookieArray.length} cookie(s)`);
      setShowBulkImport(false);
      setBulkImportJson('');
      await loadCookies();
    } catch (err) {
      console.error('Failed to import cookies:', err);
      toast.error(`Failed to import: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cookie) => {
    setCookieForm({
      name: cookie.name,
      value: cookie.value,
      domain: cookie.domain || '',
      path: cookie.path || '/',
      secure: cookie.secure !== false,
      httpOnly: cookie.httpOnly !== false,
      sameSite: cookie.sameSite || 'Lax',
      comment: cookie.comment || ''
    });
    setEditingCookie({ originalName: cookie.name, originalDomain: cookie.domain });
    setShowAddForm(false);
    setShowBulkImport(false);
  };

  const cancelEdit = () => {
    setEditingCookie(null);
    setCookieForm({
      name: '',
      value: '',
      domain: '',
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'Lax',
      comment: ''
    });
  };

  const handleParseBrowserCookies = (text) => {
    // Try to parse browser cookie string (name=value; name2=value2)
    try {
      const cookiePairs = text.split(';').map(c => c.trim()).filter(Boolean);
      const parsed = cookiePairs.map(pair => {
        const [name, ...valueParts] = pair.split('=');
        return {
          name: name.trim(),
          value: valueParts.join('=').trim(),
          path: '/',
          secure: true,
          httpOnly: false
        };
      });
      setBulkImportJson(JSON.stringify(parsed, null, 2));
    } catch (err) {
      toast.error('Failed to parse browser cookie format');
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FontAwesomeIcon icon={faCookie} />
          Cookie Manager
          <span className="text-sm text-gray-400">({cookies.length} cookies)</span>
        </h3>
        <div className="flex gap-2">
          <button
            onClick={loadCookies}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
            disabled={loading}
            title="Refresh cookies"
          >
            <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin' : ''} />
          </button>
          <button
            onClick={() => { setShowAddForm(!showAddForm); setShowBulkImport(false); setEditingCookie(null); }}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Add Cookie
          </button>
          <button
            onClick={() => { setShowBulkImport(!showBulkImport); setShowAddForm(false); setEditingCookie(null); }}
            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faFileImport} className="mr-1" />
            Import JSON
          </button>
          {cookies.length > 0 && (
            <button
              onClick={handleClearAll}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTrash} className="mr-1" />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Cookie Form */}
      {(showAddForm || editingCookie) && (
        <div className="bg-gray-700 rounded p-4 mb-4">
          <h4 className="text-white font-medium mb-3">
            {editingCookie ? 'Edit Cookie' : 'Add Cookie'}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Name *</label>
              <input
                type="text"
                value={cookieForm.name}
                onChange={(e) => setCookieForm({ ...cookieForm, name: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                placeholder="cf_clearance"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Value *</label>
              <input
                type="text"
                value={cookieForm.value}
                onChange={(e) => setCookieForm({ ...cookieForm, value: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                placeholder="abc123..."
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Domain</label>
              <input
                type="text"
                value={cookieForm.domain}
                onChange={(e) => setCookieForm({ ...cookieForm, domain: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                placeholder=".example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Path</label>
              <input
                type="text"
                value={cookieForm.path}
                onChange={(e) => setCookieForm({ ...cookieForm, path: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                placeholder="/"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-gray-300 mb-1">Comment</label>
              <input
                type="text"
                value={cookieForm.comment}
                onChange={(e) => setCookieForm({ ...cookieForm, comment: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                placeholder="Cloudflare bypass cookie"
              />
            </div>
            <div className="col-span-2 flex gap-4">
              <label className="flex items-center text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={cookieForm.secure}
                  onChange={(e) => setCookieForm({ ...cookieForm, secure: e.target.checked })}
                  className="mr-2"
                />
                Secure (HTTPS only)
              </label>
              <label className="flex items-center text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={cookieForm.httpOnly}
                  onChange={(e) => setCookieForm({ ...cookieForm, httpOnly: e.target.checked })}
                  className="mr-2"
                />
                HttpOnly
              </label>
              <label className="flex items-center text-sm text-gray-300 gap-2">
                SameSite:
                <select
                  value={cookieForm.sameSite}
                  onChange={(e) => setCookieForm({ ...cookieForm, sameSite: e.target.value })}
                  className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-600"
                >
                  <option value="">None</option>
                  <option value="Strict">Strict</option>
                  <option value="Lax">Lax</option>
                </select>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={editingCookie ? handleUpdateCookie : handleAddCookie}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {editingCookie ? 'Update' : 'Add'} Cookie
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                cancelEdit();
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Import Form */}
      {showBulkImport && (
        <div className="bg-gray-700 rounded p-4 mb-4">
          <h4 className="text-white font-medium mb-3">Bulk Import Cookies</h4>
          <p className="text-sm text-gray-400 mb-2">
            Paste JSON array of cookies, or browser cookie string (name=value; name2=value2)
          </p>
          <textarea
            value={bulkImportJson}
            onChange={(e) => setBulkImportJson(e.target.value)}
            className="w-full h-32 px-3 py-2 bg-gray-800 text-white rounded border border-gray-600 font-mono text-sm"
            placeholder='[{"name":"cf_clearance","value":"abc123","domain":".example.com","path":"/"}]'
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleBulkImport}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
              disabled={loading || !bulkImportJson.trim()}
            >
              <FontAwesomeIcon icon={faFileImport} className="mr-2" />
              Import
            </button>
            <button
              onClick={() => {
                const sample = bulkImportJson || '';
                handleParseBrowserCookies(sample);
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              disabled={!bulkImportJson.trim()}
            >
              Parse Browser Format
            </button>
            <button
              onClick={() => setShowBulkImport(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cookie List */}
      {loading && cookies.length === 0 ? (
        <div className="text-gray-400 text-center py-4">Loading cookies...</div>
      ) : cookies.length === 0 ? (
        <div className="text-gray-400 text-center py-4">
          No cookies imported yet. Add cookies to automatically inject them into all proxy requests.
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {cookies.map((cookie, idx) => (
            <div key={idx} className="bg-gray-700 rounded p-3 flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-white font-medium">{cookie.name}</span>
                  {cookie.secure && <span className="text-xs bg-green-600 px-2 py-0.5 rounded">Secure</span>}
                  {cookie.httpOnly && <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">HttpOnly</span>}
                  {cookie.sameSite && <span className="text-xs bg-purple-600 px-2 py-0.5 rounded">{cookie.sameSite}</span>}
                </div>
                <div className="text-sm text-gray-400">
                  <div className="font-mono truncate max-w-md">{cookie.value}</div>
                  <div className="mt-1">
                    {cookie.domain && <span className="mr-3"><FontAwesomeIcon icon={faGlobe} className="mr-1" />{cookie.domain}</span>}
                    {cookie.path && <span className="mr-3"><FontAwesomeIcon icon={faFolder} className="mr-1" />{cookie.path}</span>}
                  </div>
                  {cookie.comment && (
                    <div className="mt-1 text-xs italic"><FontAwesomeIcon icon={faComment} className="mr-1" />{cookie.comment}</div>
                  )}
                  {cookie.importedAt && (
                    <div className="text-xs text-gray-500 mt-1">
                      Imported: {new Date(cookie.importedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => startEdit(cookie)}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  title="Edit"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDeleteCookie(cookie.name, cookie.domain)}
                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-900 bg-opacity-30 rounded border border-blue-700">
        <p className="text-sm text-blue-300 flex items-start gap-2">
          <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5" />
          <span>
            <strong>Auto-Injection:</strong> All cookies are automatically injected into matching requests based on domain and path.
            Great for bypassing Cloudflare Turnstile or maintaining authenticated sessions!
          </span>
        </p>
      </div>
    </div>
  );
};

export default ProxyCookieManager;
